import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, CheckCircle, XCircle, Edit, Send, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_LABELS = { pending: 'Beklemede', approved: 'Onaylandi', rejected: 'Reddedildi' };

const paraBicim = (n) => Number(n || 0).toLocaleString('tr-TR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sayiBicim = (n) => Number(n || 0).toLocaleString('tr-TR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_COLORS = {
  pending: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]',
};

export default function ConsumptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/production-orders', { state: { tab: 'consumptions' } });
  const [consumption, setConsumption] = useState(null);
  usePageTitle('Sarf Detayı');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [stockWarnings, setStockWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [silOnayi, setSilOnayi] = useState(false);
  // Sunucudaki `module_permission` ile aynı ölçü: DELETE -> "delete".
  const { hasPermission } = useAuth();
  const silebilir = hasPermission('consumptions', 'delete');
  // Onaylamak/reddetmek GİRENİN değil ONAYLAYANIN işi (sunucu `approve` istiyor);
  // yetkisi olmayana 403'e götüren düğme gösterilmez.
  const onaylayabilir = hasPermission('consumptions', 'approve');

  useEffect(() => { fetchConsumption(); }, [id]);

  const fetchConsumption = () =>
    api.get(`/consumptions/${id}`).then(r => setConsumption(r.data)).catch(() => toast.error('Yuklenemedi'));

  // Onaylanmış tüketim ASLA silinmez; kural sunucuda (`DUZELTILEBILIR`),
  // düğme yalnızca onaylanmamış kayıtta görünür.
  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/consumptions/${id}`);
      toast.success('Tuketim silindi');
      navigate('/erp/production-orders', { state: { tab: 'consumptions' } });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Silinemedi');
      setSilOnayi(false);
      fetchConsumption();   // 409: bu arada onaylanmış olabilir, ekranı tazele
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (force = false) => {
    setLoading(true);
    try {
      const res = await api.patch(`/consumptions/${id}/approve`, { force });
      if (res.data.warnings && res.data.warnings.length > 0) {
        setStockWarnings(res.data.warnings);
        setShowWarningModal(true);
        setLoading(false);
        return;
      }
      toast.success('Tuketim onaylandi');
      setShowWarningModal(false);
      fetchConsumption();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Gerekce zorunlu'); return; }
    setLoading(true);
    try {
      await api.patch(`/consumptions/${id}/reject`, { rejection_reason: rejectReason });
      toast.success('Tuketim reddedildi');
      setShowRejectModal(false);
      setRejectReason('');
      fetchConsumption();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async () => {
    setLoading(true);
    try {
      await api.patch(`/consumptions/${id}/resubmit`);
      toast.success('Tekrar onaya gonderildi');
      fetchConsumption();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    } finally {
      setLoading(false);
    }
  };

  if (!consumption) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  // Fiyati olmayan kalemler toplami sessizce dusurur; sayisini yukarida yaz.
  const fiyatsizSayisi = (consumption.items || []).filter(i => !i.unit_cost).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Tuketim Detayi</h1>
            <span className={`mt-1 inline-block status-badge ${STATUS_COLORS[consumption.status]}`}>
              {STATUS_LABELS[consumption.status]}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {/* ONAYLANMAMIŞ her tüketim düzenlenebilir (beklemede + reddedilen);
              onaylanan stok ve maliyet doğurduğu için dokunulmaz. */}
          {(consumption.status === 'pending' || consumption.status === 'rejected') && (
            <Button variant="outline" onClick={() => navigate(`/erp/consumptions/${id}/edit`)}
              className="border-[#d0c4b0] text-[#3F3E39]" data-testid="edit-consumption-btn">
              <Edit size={15} className="mr-1.5" /> Duzenle
            </Button>
          )}
          {(consumption.status === 'pending' || consumption.status === 'rejected') && silebilir && (
            <Button variant="outline" onClick={() => setSilOnayi(true)}
              className="border-[#7a3535]/30 text-[#7a3535] hover:bg-[#7a3535]/10"
              data-testid="delete-consumption-btn">
              <Trash2 size={15} className="mr-1.5" /> Sil
            </Button>
          )}
          {consumption.status === 'rejected' && (
            <Button onClick={handleResubmit} disabled={loading}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
              <Send size={15} className="mr-1.5" /> Tekrar Gonder
            </Button>
          )}
          {consumption.status === 'pending' && onaylayabilir && (
            <>
              <Button variant="outline" onClick={() => setShowRejectModal(true)}
                className="border-[#7a3535]/30 text-[#7a3535] hover:bg-[#7a3535]/10">
                <XCircle size={15} className="mr-1.5" /> Reddet
              </Button>
              <Button onClick={() => handleApprove(false)} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5a3e] font-semibold">
                <CheckCircle size={15} className="mr-1.5" /> Onayla
              </Button>
            </>
          )}
        </div>
      </div>

      {consumption.status === 'rejected' && consumption.rejection_reason && (
        <div className="mb-4 p-4 bg-[#7a3535]/10 border border-[#7a3535]/30 rounded-lg">
          <p className="text-sm font-semibold text-[#7a3535] mb-1">Red Gerekçesi:</p>
          <p className="text-sm text-[#7a3535]">{consumption.rejection_reason}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <InfoCard label="Depo" value={consumption.warehouse_name} />
        <InfoCard label="Kategori" value={consumption.category_label} />
        {/* Is tarihi gosterilir; eski kayitlarda kayit gunune dusulur */}
        <InfoCard label="Tarih"
          value={new Date(consumption.consumption_date || consumption.created_at).toLocaleDateString('tr-TR')} />
        <InfoCard label="Urun Sayisi" value={consumption.items?.length || 0} />
        <InfoCard label="Ad" value={consumption.name || '-'} />
        <InfoCard label="Porsiyon Adedi"
          value={consumption.portion_count != null ? consumption.portion_count : '-'} />
        <InfoCard label={consumption.maliyet_tahmini ? 'Maliyet (tahmini)' : 'Maliyet'}
          value={consumption.total_cost != null
            ? `${Number(consumption.total_cost).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
            : '-'} />
      </div>

      <Card className="bg-[#faf7f2] border-[#d0c4b0] mb-6">
        <CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider text-[#8a7e70]">Aciklama</CardTitle></CardHeader>
        <CardContent><p className="text-[#1a1916]">{consumption.description}</p></CardContent>
      </Card>

      {/* MAMULUN MALIYETI RECETEDEN gelir (sunucu: `urun_birim_maliyeti`).
          Uyari artik "alis fiyati yok" demiyor: mamulun zaten alis fiyati
          olmaz, ureti liyor. Gercek eksik, NE alis fiyati NE recete olmasi. */}
      {fiyatsizSayisi > 0 && (
        <div className="mb-4 p-3 bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 rounded-lg text-sm text-[#7a6a2e]">
          <b>{fiyatsizSayisi} üründe maliyet bulunamadı.</b> Bu ürünlerin ne son
          alış fiyatı ne de reçetesi var; satırlar 0 TL sayıldığı için toplam
          maliyet olduğundan düşük. Ürün kartına fiyat girin ya da reçete tanımlayın.
        </div>
      )}

      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader><CardTitle className="text-sm font-bold uppercase tracking-wider text-[#8a7e70]">Tuketilen Urunler</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kod</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">
                  Birim Fiyat
                </TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">
                  Maliyet{consumption.maliyet_tahmini ? ' (tahmini)' : ''}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consumption.items?.map((item, idx) => {
                // Birim fiyat ANA BIRIM basina; girilen birim farkliysa
                // cevrilmis miktari da goster ki carpim tutsun.
                const cevrildi = item.unit_main && item.unit_main !== item.unit
                  && item.quantity_main != null;
                return (
                  <TableRow key={idx} className="border-[#d0c4b0]">
                    <TableCell className="font-medium text-[#1a1916]">
                      {item.product_name}
                      {item.unit_error && (
                        <span className="ml-2 text-[11px] text-[#7a3535]" title={item.unit_error}>
                          birim çevrilemedi
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-mono text-[#8a7e70]">{item.product_code}</TableCell>
                    <TableCell className="text-right font-semibold text-[#1a1916]">
                      {sayiBicim(item.quantity)}
                      {cevrildi && (
                        <div className="text-[11px] font-normal text-[#8a7e70]">
                          = {sayiBicim(item.quantity_main)} {item.unit_main}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-[#8a7e70]">{item.unit}</TableCell>
                    <TableCell className="text-right font-mono text-[#3F3E39]">
                      {item.unit_cost
                        ? <>{paraBicim(item.unit_cost)}
                            <span className="text-[11px] text-[#8a7e70]"> /{item.unit_main || item.unit}</span>
                            {/* "Bu rakam nereden geldi": recete maliyeti ile
                                alis fiyatini ayirt etmeden gosterince mamulun
                                maliyeti uydurulmus gibi duruyordu. */}
                            {item.maliyet_kaynagi === 'recete' && (
                              <div className="text-[10px] text-[#2e6fa0]">reçeteden</div>
                            )}</>
                        : <span className="text-[11px] text-[#7a3535]">maliyet yok</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#1a1916]">
                      {paraBicim(item.total_cost)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {consumption.items?.length > 0 && (
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0] border-[#d0c4b0]">
                  <TableCell colSpan={5} className="text-right font-bold text-[#1a1916] uppercase text-xs tracking-wider">
                    Toplam{consumption.maliyet_tahmini ? ' (tahmini)' : ''}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-[#1a1916]">
                    {paraBicim(consumption.total_cost)} TL
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {silOnayi && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl border border-[#d0c4b0]">
            <h2 className="text-lg font-bold text-[#1a1916] mb-2">Tuketimi Sil</h2>
            <p className="text-sm text-[#3F3E39] mb-1">{consumption.name}</p>
            <p className="text-sm text-[#6b6255]">
              Kayıt <b>kalıcı olarak silinir</b> ve geri getirilemez. Tüketim onaylanmadığı
              için stoktan hiçbir şey düşülmemiştir; stok etkilenmez.
            </p>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setSilOnayi(false)}
                className="border-[#d0c4b0] text-[#3F3E39]">
                Vazgec
              </Button>
              <Button onClick={handleDelete} disabled={loading}
                className="bg-[#7a3535] text-white hover:bg-[#5a2525] font-semibold"
                data-testid="delete-consumption-confirm">
                <Trash2 size={15} className="mr-1.5" /> {loading ? 'Siliniyor…' : 'Sil'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl border border-[#d0c4b0]">
            <h2 className="text-lg font-bold text-[#1a1916] mb-4">Tuketimi Reddet</h2>
            <label className="block text-sm font-medium text-[#3F3E39] mb-2">Red Gerekçesi *</label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reddetme sebebini yazin..."
              className="w-full rounded-md border border-[#d0c4b0] px-3 py-2 text-sm text-[#1a1916] placeholder:text-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#2e2d29] resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="border-[#d0c4b0] text-[#3F3E39]">
                Iptal
              </Button>
              <Button onClick={handleReject} disabled={loading}
                className="bg-[#7a3535] text-white hover:bg-[#5a2525] font-semibold">
                Reddet
              </Button>
            </div>
          </div>
        </div>
      )}

      {showWarningModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl border border-[#d0c4b0]">
            <h2 className="text-lg font-bold text-[#1a1916] mb-2">Stok Uyarisi</h2>
            <p className="text-sm text-[#8a7e70] mb-4">Asagidaki urunler icin yeterli stok yok. Yine de onaylamak istiyor musunuz?</p>
            <div className="space-y-2 mb-4">
              {stockWarnings.map((w, i) => (
                <div key={i} className="p-3 bg-[#7a6a2e]/10 rounded-lg text-sm">
                  <span className="font-semibold text-[#1a1916]">{w.product_name}</span>
                  <span className="text-[#8a7e70]"> — Mevcut: {w.available} {w.unit}, Istenen: {w.requested} {w.unit}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowWarningModal(false)}
                className="border-[#d0c4b0] text-[#3F3E39]">
                Iptal
              </Button>
              <Button onClick={() => handleApprove(true)} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5a3e] font-semibold">
                Evet, Onayla
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardContent className="p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#8a7e70] mb-1">{label}</p>
        <p className="text-lg font-semibold text-[#1a1916]">{value}</p>
      </CardContent>
    </Card>
  );
}
